import { useMemo } from 'react';
import { ItemTile, GroupedItemList } from '@/components/ui';
import { formatDateKey, expandItemsToEntries, type DisplayEntry } from '@/lib/itemListUtils';
import type { TaskItem } from '@perfin/shared';
import type { GroupBy, TabKey } from '@/stores/uiStore';

interface ChoreWheelListProps {
  tasks: TaskItem[];
  groupBy?: GroupBy;
  currentTab: TabKey;
  hideStatusToggle?: boolean;
  referenceDate: Date;
  statusFilter?: string | null;
}

export function ChoreWheelList({ tasks, groupBy, currentTab, hideStatusToggle, referenceDate, statusFilter }: ChoreWheelListProps) {
  const displayEntries = useMemo(() => {
    return expandItemsToEntries(tasks, currentTab, referenceDate, statusFilter);
  }, [tasks, currentTab, referenceDate, statusFilter]);

  // Helper to render item tile with occurrenceDate
  const renderTile = (entry: DisplayEntry<TaskItem>) => (
    <ItemTile
      key={entry.key}
      item={entry.item}
      linkPath={`/tasks/${entry.item.id}`}
      currentTab={currentTab}
      occurrenceDate={entry.occurrenceDate}
      hideStatusToggle={hideStatusToggle}
    />
  );

  // Date-based grouping
  if (groupBy === 'date' && currentTab !== 'all') {
    const dateGroups: Record<string, { entries: DisplayEntry<TaskItem>[]; timestamp: number }> = {};

    for (const entry of displayEntries) {
      const effectiveDate = entry.occurrenceDate;
      const dateKey = effectiveDate ? formatDateKey(effectiveDate) : 'No Date';
      const timestamp = effectiveDate ? effectiveDate.getTime() : 0;

      if (!dateGroups[dateKey]) {
        dateGroups[dateKey] = { entries: [], timestamp };
      }
      dateGroups[dateKey].entries.push(entry);
    }

    const sortedGroups = Object.entries(dateGroups).sort(([, a], [, b]) => a.timestamp - b.timestamp);

    return (
      <>
        {sortedGroups.map(([dateKey, { entries }]) => (
          <div key={dateKey} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              {dateKey} ({entries.length})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {entries.map(renderTile)}
            </div>
          </div>
        ))}
      </>
    );
  }

  // Other groupBy options
  if (groupBy) {
    return (
      <GroupedItemList
        items={displayEntries.map(e => e.item)}
        groupBy={groupBy}
        renderGroup={(key, groupTasks) => {
          const entriesForGroup = displayEntries.filter(e => groupTasks.includes(e.item));
          return (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                {key} ({entriesForGroup.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {entriesForGroup.map(renderTile)}
              </div>
            </div>
          );
        }}
        renderItem={(task) => {
          const entry = displayEntries.find(e => e.item.id === task.id) || { item: task, key: task.id, occurrenceDate: undefined };
          return renderTile(entry);
        }}
      />
    );
  }

  // No grouping
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {displayEntries.map(renderTile)}
    </div>
  );
}
